import { useEffect, useState } from 'react';
import { Check, Trash2 } from 'lucide-react';
import { subscribeToTasks, addTask, setTaskDone, deleteTask } from '../services/leadService';
import { fromDateInputValue, isDueToday, isOverdue, toDateInputValue } from '../utils/date';
import type { LeadTask } from '../types/lead.types';

interface Props {
  leadId: string;
  createdBy: string;
}

/** Lista dei task (promemoria con scadenza) di un singolo lead, usata dentro
 * LeadDetailPanel — separata dalle note libere (LeadActivity): un task si
 * segna fatto o si elimina, una nota resta un log immutabile. */
export function TaskList({ leadId, createdBy }: Props) {
  const [tasks, setTasks] = useState<LeadTask[]>([]);
  const [text, setText] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => subscribeToTasks(leadId, setTasks), [leadId]);

  const openTasks = tasks.filter((t) => !t.done);
  const doneTasks = tasks.filter((t) => t.done);

  const handleAdd = async () => {
    if (!text.trim() || !dueDate) return;
    const due = fromDateInputValue(dueDate);
    if (!due) return;
    await addTask(leadId, { text: text.trim(), dueAt: due }, createdBy);
    setText('');
    setDueDate('');
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Es. Richiamare per la proposta"
          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-w-0"
        />
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm w-36 shrink-0"
        />
        <button
          onClick={handleAdd}
          disabled={!text.trim() || !dueDate}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 disabled:opacity-50 shrink-0"
        >
          Aggiungi
        </button>
      </div>

      {openTasks.length === 0 && doneTasks.length === 0 && (
        <p className="text-xs text-gray-400">Nessun task per questo lead.</p>
      )}

      <ul className="space-y-1.5">
        {openTasks.map((task) => {
          const overdue = isOverdue(task.dueAt);
          const dueToday = isDueToday(task.dueAt);
          return (
            <li
              key={task.id}
              className={`flex items-center gap-2 rounded-lg p-2 text-sm ${
                overdue ? 'bg-red-50 text-red-700' : dueToday ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-700'
              }`}
            >
              <button
                onClick={() => setTaskDone(leadId, task.id, true)}
                title="Segna fatto"
                className="shrink-0 w-5 h-5 rounded-full border border-current flex items-center justify-center hover:bg-white/50"
              >
                <Check size={11} />
              </button>
              <span className="flex-1 truncate">{task.text}</span>
              <span className="text-[10px] shrink-0 opacity-75">{formatDueLabel(task.dueAt)}</span>
              <button onClick={() => deleteTask(leadId, task.id)} title="Elimina task" className="shrink-0 text-gray-400 hover:text-red-600">
                <Trash2 size={12} />
              </button>
            </li>
          );
        })}
      </ul>

      {doneTasks.length > 0 && (
        <details className="mt-2">
          <summary className="text-[10px] uppercase text-gray-400 cursor-pointer">{doneTasks.length} completati</summary>
          <ul className="space-y-1 mt-1.5">
            {doneTasks.map((task) => (
              <li key={task.id} className="flex items-center gap-2 rounded-lg p-2 text-sm bg-gray-50 text-gray-400 line-through">
                <span className="flex-1 truncate">{task.text}</span>
                <button onClick={() => deleteTask(leadId, task.id)} title="Elimina task" className="shrink-0 no-underline text-gray-400 hover:text-red-600">
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function formatDueLabel(dueAt: unknown): string {
  if (isOverdue(dueAt)) return 'In ritardo';
  if (isDueToday(dueAt)) return 'Oggi';
  const iso = toDateInputValue(dueAt);
  return iso ? iso.split('-').reverse().join('.') : '';
}
